



























































import { Component, Vue, Ref, Prop } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import {
  ComBox,
  getComBoxOffsetToUtc,
  getComBoxDryingPrograms,
  doesComBoxSupportRoomDrying,
  doesComBoxTargetDrying,
  doesComBoxSupportAdditionalSensors,
  getSensorTypes,
  isRoomDryingProgram,
  isHyDryMini,
  getSerialForSensorIndex,
} from "@/data/models/ComBox";
import {
  ComBoxSensorUsage,
  ControlConfig,
  getControlConfig,
  getDefaultSocketModesForDryingProgram,
  getFallbackControlConfig,
  isControlConfigRecord,
} from "@/data/models/ControlConfig";

import ControlConfigComponent, {
  ControlConfigUpdate,
} from "@/components/combox/ControlConfig.vue";
import SensorStatusComponent from "@/components/combox/SensorStatus.vue";
import { ComBoxDryingProgram, MINI_SUPPORTED_DRYING_PROGRAMS } from "@/data/models/ComBoxDryingProgram";
import { ComBoxSensorType } from "@/data/models/ComBoxSensorData";
import {
  chooseStartDryingProgram,
  ClientDryingPrograms,
  ComBoxClientConfig,
  getClientDryingPrograms,
  getComBoxClientConfig,
} from "@/data/models/ComBoxClientConfig";
import {
  getClientId,
} from "@/data/repository/ClientRepository";
import ConfirmationDialogComponent from "../common/ConfirmationDialog.vue";
import { getClientConfigInfo, hasOwnMiniClientConfigInfo } from "@/data/repository/SettingsRepository";
import {
  getDefaultSensorConfig,
  switchSensorConfig,
  validateSensorConfig,
} from "@/utils/combox/SensorConfigValidation";

@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "sensor-status": SensorStatusComponent,
    "control-config": ControlConfigComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class CreateControlConfigComponent extends Vue {
  @Prop()
  comBox!: ComBox;

  @Prop({ default: true })
  showCompleteControlConfigOptions!: boolean;

  @Prop({ default: () => new Map() })
  sensorLegendMetadata!: Map<number, { serialNumber?: string }>;

  @Ref("baseDialog")
  baseDialogComponent!: BaseDialogComponent;

  @Ref("controlConfig")
  controlConfigComponent!: ControlConfigComponent;

  @Ref("confirmationDialog")
  confirmationDialog!: ConfirmationDialogComponent;

  comBoxDryingPrograms: ComBoxDryingProgram[] = [];
  selectableComBoxDryingPrograms: ComBoxDryingProgram[] = [];
  selectableDryingPrograms: number[] = [];

  confirmationMessage = "";
  startComBox = true;

  resolve?: (value: ControlConfig) => void = undefined;
  reject?: (reason: string) => void = undefined;

  dryingProgramNumber = 0;

  get supportsAdditionalSensors() {
    if (!this.comBox) {
      return false;
    }

    return doesComBoxSupportAdditionalSensors(this.comBox);
  }

  get sensorTypes() {
    return getSensorTypes(this.comBox.comBoxStatus);
  }

  get totalNumberOfSensors() {
    return this.supportsAdditionalSensors ? 9 : 5;
  }

  get isMini() {
    return isHyDryMini(this.comBox);
  }

  /** Builds sensor serial map from sensorSlotSerials (persisted by Cloud from IoT Type 22 payloads). */
  get sensorSerials(): Map<number, string> {
    const result = new Map<number, string>();
    const slotSerials = this.comBox?.comBoxStatus?.sensorSlotSerials;
    if (slotSerials) {
      for (let i = 0; i < this.totalNumberOfSensors; i++) {
        const serial = getSerialForSensorIndex(slotSerials, i);
        if (serial) {
          result.set(i, serial);
        }
      }
    }
    return result;
  }

  sensorUsage =
    this.supportsAdditionalSensors
      ? Array(9).fill(ComBoxSensorUsage.PROGRAM).map((v, i) => (i === 4 ? ComBoxSensorUsage.REFERENCE : v))
      : [
          ComBoxSensorUsage.PROGRAM,
          ComBoxSensorUsage.PROGRAM,
          ComBoxSensorUsage.PROGRAM,
          ComBoxSensorUsage.PROGRAM,
          ComBoxSensorUsage.REFERENCE,
        ];

  needsReferenceSensor = false;

  clientConfig: ComBoxClientConfig | null = null;

  /** The programs the client lets this device start, as the client config dialog shows them. */
  clientDryingPrograms: ClientDryingPrograms = getClientDryingPrograms(null, false, false);

  /** A drying runs, but the box stored no readable control config, so the dialog shows a default. */
  currentConfigUnknown = false;

  saveDisabled = true;
  showInstructions = false;
  hasUnsavedChanges = false;
  initialControlConfigJson = "";

  getBaseControlConfig(comBox: ComBox) {
    const currentControlConfig = comBox.currentControlConfig;

    if (
      currentControlConfig &&
      comBox.comBoxStatus.dryRunActive &&
      isControlConfigRecord(currentControlConfig)
    ) {
      return getControlConfig(
        currentControlConfig,
        getComBoxOffsetToUtc(this.comBox),
        comBox.serialNo
      );
    }

    if (
      this.clientConfig &&
      this.comBox.currentAssignment?.clientId === getClientId()
    ) {
      return this.clientConfig.defaultControlConfig;
    }

    const defaultControlConfig = comBox.defaultControlConfig;

    if (defaultControlConfig && isControlConfigRecord(defaultControlConfig)) {
      return getControlConfig(defaultControlConfig, 0, comBox.serialNo);
    }

    // Most boxes store their client config here instead (see isControlConfigRecord):
    // start from the viewer's own client default, IRES's for an IRES user.
    return this.clientConfig?.defaultControlConfig ??
      getFallbackControlConfig(
        chooseStartDryingProgram(
          this.clientDryingPrograms.defaultProgram,
          this.clientDryingPrograms.defaultProgram,
          this.selectableDryingPrograms
        ),
        this.sensorUsage
      );
  }

  onDialogOpened() {
    const currentControlConfig = this.comBox.currentControlConfig;

    this.currentConfigUnknown =
      !!this.comBox.comBoxStatus.dryRunActive &&
      !(currentControlConfig && isControlConfigRecord(currentControlConfig));

    const baseControlConfig = this.getBaseControlConfig(this.comBox);

    // Starting, or the running program is unknown: never preselect a program the list does not
    // offer (a Box config's Unterdruck on a Mini, a client default the client no longer allows).
    if (!this.comBox.comBoxStatus.dryRunActive || this.currentConfigUnknown) {
      baseControlConfig.dryingProgram = chooseStartDryingProgram(
        baseControlConfig.dryingProgram,
        this.clientDryingPrograms.defaultProgram,
        this.selectableDryingPrograms
      );
    }

    if (!this.comBox.comBoxStatus.dryRunActive) {
      //Set default socket modes if starting new dry run
      const {
        socket1Mode,
        socket2Mode,
      } = getDefaultSocketModesForDryingProgram(
        baseControlConfig.dryingProgram
      );

      baseControlConfig.socket1Mode = socket1Mode;
      baseControlConfig.socket2Mode = socket2Mode;
    }

    if (baseControlConfig.dryingProgram >= 8) {
      baseControlConfig.dryingProgram = 0;
    }

    // Only apply overrides when starting a new dry run;
    // during an active dry run, preserve the values from currentControlConfig.
    if (isHyDryMini(this.comBox) && !this.comBox.comBoxStatus.dryRunActive) {
      const miniDefaultProgram = chooseStartDryingProgram(
        this.clientDryingPrograms.defaultProgram,
        this.clientDryingPrograms.defaultProgram,
        this.selectableDryingPrograms
      );
      baseControlConfig.dryingProgram = miniDefaultProgram;
      baseControlConfig.sensorUsage = Array(this.totalNumberOfSensors).fill(ComBoxSensorUsage.MEASUREMENT);

      const { socket1Mode, socket2Mode } = getDefaultSocketModesForDryingProgram(miniDefaultProgram);
      baseControlConfig.socket1Mode = socket1Mode;
      baseControlConfig.socket2Mode = socket2Mode;
    }

    const program = this.comBoxDryingPrograms[
      baseControlConfig.dryingProgram
    ];

    this.needsReferenceSensor = program
      ? program.needsReferenceSensor
      : baseControlConfig.dryingProgram === 2;

    this.dryingProgramNumber = baseControlConfig.dryingProgram;

    this.$nextTick(() => {
      if (this.comBox.comBoxStatus.dryRunActive && !this.currentConfigUnknown) {
        // EDIT: trust the device's current sensor config
        this.sensorUsage = baseControlConfig.sensorUsage.slice(0, this.totalNumberOfSensors);
      } else {
        // START: compute fresh defaults from program rules + connected hardware
        this.sensorUsage = getDefaultSensorConfig(
          baseControlConfig.dryingProgram,
          this.sensorTypes,
          this.totalNumberOfSensors,
          this.sensorSerials,
          this.isMini,
        );
      }
      this.controlConfigComponent.displayControlConfig(baseControlConfig);

      this.$nextTick(() => {
        if (this.isChangeDryRunMode) {
          this.captureInitialControlConfig();
        }
      });
    });
  }

  getSelectableComBoxDryingPrograms(
    comBox: ComBox,
    allComBoxDryingPrograms: ComBoxDryingProgram[]
  ) {
    const supportsRoomDrying = doesComBoxSupportRoomDrying(comBox);
    const supportsTargetDrying = doesComBoxTargetDrying(comBox);

    if (isHyDryMini(comBox)) {
      return allComBoxDryingPrograms.filter(
        (program) =>
          MINI_SUPPORTED_DRYING_PROGRAMS.includes(program.programNumber) &&
          this.clientDryingPrograms.selectablePrograms.includes(program.programNumber)
      );
    }

    const supportedPrograms = new Set([0, 1, 2, 3]);

    if (supportsRoomDrying) {
      supportedPrograms.add(4);
    }

    if (supportsTargetDrying) {
      supportedPrograms.add(5);
    }

    return allComBoxDryingPrograms.filter(
      (program) =>
        supportedPrograms.has(program.programNumber) &&
        //!program.locked &&
        this.clientDryingPrograms.selectablePrograms.includes(program.programNumber)
    );
  }

  getSelectableDryingPrograms(
    selectedComBoxDryingPrograms: ComBoxDryingProgram[]
  ) {
    const selectableDryingPrograms: number[] = [];

    selectedComBoxDryingPrograms.forEach((program) => {
      selectableDryingPrograms.push(program.programNumber);
    });

    return selectableDryingPrograms;
  }

  async show(startComBox: boolean, confirmationMessage: string) {
    this.startComBox = startComBox;
    this.hasUnsavedChanges = false;
    this.initialControlConfigJson = "";
    this.saveDisabled = !startComBox;
    this.showInstructions = startComBox;

    this.confirmationMessage = confirmationMessage;
    this.initialUpdate = true;

    const isMini = isHyDryMini(this.comBox);
    const clientConfigInfo = getClientConfigInfo(isMini);

    this.clientConfig = clientConfigInfo
      ? getComBoxClientConfig(clientConfigInfo, { serialNumber: this.comBox.serialNo })
      : null;
    this.clientDryingPrograms = getClientDryingPrograms(
      this.clientConfig,
      isMini,
      hasOwnMiniClientConfigInfo()
    );

    this.comBoxDryingPrograms = getComBoxDryingPrograms(this.comBox, this);
    this.selectableComBoxDryingPrograms = this.getSelectableComBoxDryingPrograms(
      this.comBox,
      this.comBoxDryingPrograms
    );

    this.selectableDryingPrograms = this.getSelectableDryingPrograms(
      this.selectableComBoxDryingPrograms
    );

    this.baseDialogComponent.show({
      styleAsAlertDialog: true,
      maxWidth: 620,
      title: this.$t(
        startComBox
          ? "comBoxDetail.controlConfig.startDryRun"
          : "comBoxDetail.controlConfig.changeDryRun",
        [this.comBox.serialNo]
      ).toString(),
      negativeButtonText: this.getNegativeButtonText(),
      positiveButtonText: this.$t(
        startComBox
          ? "comBoxDetail.controlConfig.start"
          : "common.save"
      ).toString(),
      persistent: this.isPersistent,
      closeOnSave: false,
    });

    return new Promise<ControlConfig>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  getDefaultProgramName(programNumber: number) {
    if (programNumber === 0) {
      return this.$t("comBoxDetail.monitor").toString();
    }

    if (programNumber === 1) {
      return this.$t("comBoxDetail.programDryingTime").toString();
    }

    if (programNumber === 2) {
      return this.$t("comBoxDetail.programDryingSensor").toString();
    }

    return this.$t("comBoxDetail.formatProgram", [programNumber]).toString();
  }

  async onSave() {
    const controlConfig = this.collectControlConfig();

    let didConfirmStart = true;

    const { dryingProgram } = controlConfig;

    if (dryingProgram === 0 || dryingProgram === 1 || dryingProgram === 2) {
      // ==================== FIRMWARE v4.0.0+ CHANGES ====================
      const validSensorIndices = Array.from({ length: this.totalNumberOfSensors }, (_, i) => i)
        .filter((i) => i !== 4);

      const hasConnectedFloorSensor = validSensorIndices.some(
        (i) => this.sensorTypes[i] !== ComBoxSensorType.EMPTY
      );

      if (!hasConnectedFloorSensor) {
        const supportsRoomDrying = doesComBoxSupportRoomDrying(this.comBox);

        if (supportsRoomDrying) {
          didConfirmStart = await this.confirmationDialog.show({
            title: this.$t(
              "comBoxDetail.controlConfig.missingFloorSensorWarningTitle"
            ).toString(),
            message: this.$t(
              "comBoxDetail.controlConfig.missingFloorSensorWarningMessageForBoxSupportingRoomDrying"
            ).toString(),
            acceptMessage: this.$t(
              "comBoxDetail.controlConfig.missingFloorSensorWarningStartAnyway"
            ).toString(),
            rejectMessage: this.$t("common.back").toString(),
          });

          if (!didConfirmStart) {
            this.controlConfigComponent.onNewDryingProgramPicked(4);
          }
        } else {
          didConfirmStart = await this.confirmationDialog.show({
            title: this.$t(
              "comBoxDetail.controlConfig.missingFloorSensorWarningTitle"
            ).toString(),
            message: this.$t(
              "comBoxDetail.controlConfig.missingFloorSensorWarningMessageForBoxDefault"
            ).toString(),
            acceptMessage: this.$t(
              "comBoxDetail.controlConfig.missingFloorSensorWarningStartAnyway"
            ).toString(),
            rejectMessage: this.$t("common.back").toString(),
          });
        }
      }
    }

    if (didConfirmStart) {
      this.baseDialogComponent.isShowing = false;
      this.resolve && this.resolve(controlConfig);
    }
  }

  /**
   * Assembles the final ControlConfig by merging the child component's config
   * with the sensor usage state managed at this level.
   *
   * Sensors whose type is EMPTY or UNKNOWN are forced to NOT_USED so the
   * device doesn't act on disconnected slots. SHT3X sensors (room sensor and
   * compatible externals) are intentionally kept — firmware v4.0.0+ encodes
   * their usage in the extended sensor bitfield.
   */
  collectControlConfig(): ControlConfig {
    const controlConfig = this.controlConfigComponent.collectControlConfig();

    // Final validation pass before sending to device
    const validatedUsage = validateSensorConfig(
      this.sensorUsage,
      controlConfig.dryingProgram,
      this.needsReferenceSensor,
      this.sensorTypes,
      this.sensorSerials,
      this.isMini,
    );

    controlConfig.sensorUsage = validatedUsage;

    return controlConfig;
  }

  onChangeSensorUsage(index: number, isRoomSensor: boolean) {
    // Room sensor (index 4) is excluded from the display sensors array,
    // therefore, we have to manually shift last 4 external sensors
    const calculatedIndex = isRoomSensor
      ? 4
      : index < 4 ? index : index + 1;

    const result = switchSensorConfig(
      calculatedIndex,
      this.sensorUsage,
      this.dryingProgramNumber,
      this.needsReferenceSensor,
      this.sensorTypes,
      this.sensorSerials,
      this.isMini,
    );

    // Only update if something actually changed
    if (result !== this.sensorUsage) {
      this.sensorUsage = result;

      if (this.isChangeDryRunMode) {
        this.refreshUnsavedChangesState();
      } else {
        this.markAsChanged();
      }
    }
  }

  initialUpdate = true;

  onControlConfigChanged(controlConfigUpdate: ControlConfigUpdate) {
    this.dryingProgramNumber = controlConfigUpdate.newValue.dryingProgram;
    this.initialUpdate = controlConfigUpdate.initialUpdate ?? this.initialUpdate;

    if (this.initialUpdate) {
      this.initialUpdate = false;
      return;
    }

    if (this.isChangeDryRunMode) {
      this.refreshUnsavedChangesState();
    } else {
      this.markAsChanged();
    }

    const oldDryingProgram = controlConfigUpdate.oldValue.dryingProgram;
    const oldDryingMode = controlConfigUpdate.oldValue.dryingMode;
    const newDryingProgram = controlConfigUpdate.newValue.dryingProgram;
    const newDryingMode = controlConfigUpdate.newValue.dryingMode;

    if (
      oldDryingProgram === newDryingProgram &&
      oldDryingMode === newDryingMode
    ) {
      return;
    }

    const dryingProgram = this.comBoxDryingPrograms[newDryingProgram];
    this.needsReferenceSensor = dryingProgram
      ? dryingProgram.needsReferenceSensor
      : newDryingProgram === 2;

    if (isRoomDryingProgram(newDryingProgram)) {
      const roomDryingHintMessageKey = this.isMini
        ? "comBoxDetail.controlConfig.roomDryingHintMessageMini"
        : "comBoxDetail.controlConfig.roomDryingHintMessageBox";

      this.confirmationDialog.show({
        title: this.$t(
          "comBoxDetail.controlConfig.roomDryingHintTitle"
        ).toString(),
        message: this.$t(roomDryingHintMessageKey).toString(),
        acceptMessage: this.$t("common.ok").toString(),
        rejectMessage: null,
      });
    }

    this.sensorUsage = getDefaultSensorConfig(
      newDryingProgram,
      this.sensorTypes,
      this.totalNumberOfSensors,
      this.sensorSerials,
      this.isMini,
    );
  }

  get isChangeDryRunMode() {
    return !this.startComBox;
  }

  get isPersistent() {
    if (this.startComBox) {
      return true;
    }

    return this.hasUnsavedChanges;
  }

  getNegativeButtonText() {
    if (this.isChangeDryRunMode && this.hasUnsavedChanges) {
      return this.$t("common.abort").toString();
    }

    return this.$t("common.close").toString();
  }

  markAsChanged() {
    this.saveDisabled = false;
    this.showInstructions = true;

    if (this.isChangeDryRunMode) {
      this.hasUnsavedChanges = true;
      this.updateDialogExitBehavior();
    }
  }

  updateDialogExitBehavior() {
    this.baseDialogComponent.negativeButtonText = this.getNegativeButtonText();
    this.baseDialogComponent.persistent = this.isPersistent;
  }

  serializeControlConfig(controlConfig: ControlConfig) {
    return JSON.stringify({
      ...controlConfig,
      sensorUsage: [...controlConfig.sensorUsage],
      nightModeActiveDays: [...controlConfig.nightModeActiveDays],
    });
  }

  captureInitialControlConfig() {
    this.initialControlConfigJson = this.serializeControlConfig(this.collectControlConfig());
    this.hasUnsavedChanges = false;
    this.saveDisabled = true;
    this.showInstructions = false;
    this.updateDialogExitBehavior();
  }

  refreshUnsavedChangesState() {
    if (!this.initialControlConfigJson) {
      return;
    }

    const currentControlConfigJson = this.serializeControlConfig(this.collectControlConfig());

    this.hasUnsavedChanges = currentControlConfigJson !== this.initialControlConfigJson;
    this.saveDisabled = !this.hasUnsavedChanges;
    this.showInstructions = this.hasUnsavedChanges;
    this.updateDialogExitBehavior();
  }

  async onNegativeAction() {
    if (!this.isChangeDryRunMode || !this.hasUnsavedChanges) {
      this.baseDialogComponent.isShowing = false;
      return;
    }

    const didConfirmAbort = await this.confirmationDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      rejectMessage: this.$t("common.back").toString(),
      acceptMessage: this.$t("common.abort").toString(),
    });

    if (didConfirmAbort) {
      this.baseDialogComponent.isShowing = false;
    }
  }
}
